import {
  DotsThreeOutlineVertical,
  DownloadSimple,
  Info,
  Trash,
} from '@phosphor-icons/react';
import { StatusCodes } from 'http-status-codes';
import { MouseEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DirectoryPathProps } from '../../TabDisplay';
import MoveFolderModal from '../modal/MoveFolderModal';
import RenameFolderModal from '../modal/RenameFolderModal';

import {
  axiosInstanceForPublicAPI,
  axiosInstanceForTenantAPI,
} from '~/assets/utils/axiosInstance';
import { isRESTAPIError } from '~/assets/utils/helpers';
import { Button } from '~/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover';
import { useAuth } from '~/contexts/auth/authContext';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import ErrorType from '~/interfaces/ErrorType';
import {
  AttachmentFile,
  AttachmentFolder,
  ReportAttachementProps,
} from '~/services/api/reports/types';
import { setDisplayFile, setFileDetails } from '~/slices/reportDataSlice';
import { RootState } from '~/utils/store';

type FileOrFolder = AttachmentFile | AttachmentFolder;

interface OtherOptionPopoverProps {
  absolute?: boolean;
  iconSize?: number;
  reportId: string | undefined;
  attachments: AttachmentFolder;
  attachmentsDisplay: FileOrFolder[];
  directoryPath?: DirectoryPathProps[] | undefined;
  itemInformation: ReportAttachementProps;
  deleteFolderAction: (
    items: {
      parentFolder?: number;
      fileId: number;
      isFolder: boolean;
    }[],
  ) => void;
}

const OtherOptionsPopoverFile = ({
  absolute = false,
  iconSize = 22,
  reportId,
  attachments,
  attachmentsDisplay,
  directoryPath,
  itemInformation,
  deleteFolderAction,
}: OtherOptionPopoverProps) => {
  const dispatch = useDispatch();
  const { slug } = useAuth();
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const { openConfirmationModal } = useToastNotificationContext();
  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );

  const [open, setOpen] = useState(false);
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [isDownloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const handleDeleteCheckedItems = () => {
    openConfirmationModal(
      'Remove File Confirmation',
      'Please confirm if you want to remove this file.',
      'Cancel',
      'Confirm',
      () => {
        deleteFolderAction([
          {
            fileId: itemInformation.id,
            isFolder: itemInformation.is_folder,
          },
        ]);
      },
    );
  };

  const addAuditLog = async () => {
    try {
      const newdata = {
        activity: `${itemInformation.name}`,
      };
      const response = await axiosInstanceForTenantAPI(companySlug).post(
        `/report/create/download-audit-log/${reportId}`,
        newdata,
      );
      const res = response.data;
      return res;
    } catch (err) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened. We\'re currently fixing it.',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You are unauthorized to update report',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You cannot use this request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      createToastNotification(errorMessage, 'error');
    }
  };

  const downloadAttachment = async () => {
    if (userAccessRoles.is_allowed_to_download_attachments) {
      setDownloading(true);
      try {
        axiosInstanceForPublicAPI()
          .get(itemInformation.url ?? '', { responseType: 'blob' })
          .then((response) => {
            // Create blob link to download
            const downloadUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', itemInformation.name);
            // Append to html link element page
            document.body.appendChild(link);
            // Start download
            link.click();
            // Clean up and remove the link
            if (link.parentNode) link.parentNode.removeChild(link);
            if (reportId) addAuditLog();
            createToastNotification(
              'Attachment downloaded successfully.',
              'success',
            );
          })
          .catch((err: ErrorType) => {
            createToastNotification(
              'Something went wrong ' + err.message,
              'error',
            );
          })
          .finally(() => {
            setDownloading(false);
          });
      } catch (downloadError: unknown) {
        createToastNotification('Something went wrong', 'error');
        setDownloading(false);
      }
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You are not permitted to download any attachments files on this report.',
        }),
        'info',
      );
      return;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`${
            absolute ? 'absolute top-3 right-2' : ''
          } flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
        >
          <DotsThreeOutlineVertical
            size={iconSize}
            color='#434c4c'
            weight='fill'
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side='bottom'
        align='start'
        sideOffset={10}
        bleed
        className='!max-w-32'
      >
        <div className='w-full'>
          {itemInformation.is_folder && (
            <RenameFolderModal
              reportId={reportId}
              folderId={itemInformation.id}
              parentId={directoryPath?.slice(-1)[0].id}
              currentDirectoryItems={attachmentsDisplay}
            />
          )}

          {!itemInformation.is_folder && (
            <Button
              variant='soft'
              color='neutral'
              className='block !justify-start w-full !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                downloadAttachment();
                event.stopPropagation();
              }}
              startDecorator={<DownloadSimple size={22} />}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <FormattedMessage defaultMessage={'Downloading...'} />
              ) : (
                <FormattedMessage defaultMessage={'Download'} />
              )}
            </Button>
          )}

          <MoveFolderModal
            displayAsPopover
            checkedItems={[itemInformation]}
            reportId={reportId}
            attachments={attachments}
            attachmentsDisplay={attachmentsDisplay}
            directoryPath={directoryPath}
          />

          <Button
            variant='soft'
            color='neutral'
            className='block !justify-start w-full items-start !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
              dispatch(setDisplayFile(true));
              dispatch(setFileDetails(itemInformation));
            }}
            startDecorator={<Info size={20} color='#434c4c' />}
          >
            File information
          </Button>

          <hr className='border-gray-300 dark:border-gray-900' />
          <Button
            variant='soft'
            color='neutral'
            className='block !justify-start w-full items-start !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
              handleDeleteCheckedItems();
            }}
            startDecorator={<Trash size={20} color='#434c4c' />}
          >
            <FormattedMessage defaultMessage={'Remove'} />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OtherOptionsPopoverFile;
